import { useCallback } from 'react'

import { useAssignTags, useTags } from '../api'
import { AssetTag } from '../components/TableHeaderTags/TableHeaderTags.types'
import { useTagsStore } from '../store'

// Keep the export for backward compatibility during migration
export const TAG_UPDATED_EVENT = 'tag-updated'

export const useTagToggle = () => {
  const { trigger: assignTags } = useAssignTags()
  const { activeAssetData, setTagsUpdating, setActiveAssetData } = useTagsStore()
  const { mutate: refreshTags } = useTags({ page: 1, pageSize: 100 })

  const handleTagToggle = useCallback(
    (
      toggledTag: AssetTag,
      newIsSelected: boolean | undefined,
      onError?: (error: string) => void,
    ) => {
      // If no assetId is provided, don't make API call
      if (!activeAssetData || !activeAssetData.tags) return

      const assetId = activeAssetData.id
      const currentTagIds = activeAssetData.tags.map((tag) => tag.id)
      // Store original state for potential revert on error
      const originalTags = activeAssetData.tags

      // Check if trying to add a tag when already at the 10-tag limit
      if (newIsSelected && !currentTagIds.includes(toggledTag.id) && currentTagIds.length >= 10) {
        const errorMessage = 'Cannot assign tag. Asset already has the maximum of 10 tags.'
        if (onError) {
          onError(errorMessage)
        }
        return
      }

      // newIsSelected represents the desired new state, so:
      // - if newIsSelected is true, add the tag (if not already present)
      // - if newIsSelected is false, remove the tag
      const newTagIds = newIsSelected
        ? currentTagIds.includes(toggledTag.id)
          ? currentTagIds
          : [...currentTagIds, toggledTag.id]
        : currentTagIds.filter((id) => id !== toggledTag.id)

      // Update local state immediately for better UX
      if (activeAssetData) {
        const updatedTags = newIsSelected
          ? activeAssetData.tags.some((tag) => tag.id === toggledTag.id)
            ? activeAssetData.tags // Tag already exists, no change needed
            : [...activeAssetData.tags, toggledTag]
          : activeAssetData.tags.filter((tag) => tag.id !== toggledTag.id)

        setActiveAssetData({
          ...activeAssetData,
          tags: updatedTags,
        })
      }

      // Set updating state to true before API call
      setTagsUpdating(true)

      // Call the API to update the asset's tags
      assignTags(
        {
          params: {
            asset_id: Number(assetId),
          },
          data: {
            tag_ids: newTagIds.map(Number), // Ensure all IDs are numbers
          },
        },
        {
          onSuccess: () => {
            // Refresh tags data from the backend
            refreshTags()

            // Set updating state back to false after successful update
            setTagsUpdating(false)
          },
          onError: (error) => {
            console.error('Failed to update tags:', error)
            // Revert the local state change on error
            if (activeAssetData) {
              setActiveAssetData({
                ...activeAssetData,
                tags: originalTags,
              })
            }
            // Set updating state back to false on error
            setTagsUpdating(false)
          },
        },
      )
    },
    [assignTags, activeAssetData, refreshTags, setTagsUpdating, setActiveAssetData],
  )

  return { handleTagToggle }
}
